import type {MakeRequest, RestResponse} from './request'
import {dataResponseConverter} from './request'
import type {
  AdvertiseDetail,
  AdvertiseHash,
  AdvertiseListItemModel,
  AdvertiseListResponse,
  Banners,
  CheckPhonePermissions,
  FetchAdvertisesPayload,
  RemoveFromSaleType,
  RestFetchUserProductsPayload,
} from '../models'
import type {AppStorage} from '../index'
import type {Secure} from '../helpers/userSecure'
import type {FilterMapper} from './filterMapper'

export class AdvertisesApi {
  constructor(
    private makeRequest: MakeRequest,
    private storage: AppStorage,
    private secure: Secure,
    private filterMapper: FilterMapper,
  ) {}

  suggestSearch = async (query: string) => {
    //web-api/get_search_suggestions.php
    const hash = this.storage.userHash!
    const secure = this.secure.createUserSecure()
    return this.makeRequest.request<any, any>({
      api: 'v1/search_suggestions.php',
      body: {
        phrase: query,
        lang: this.storage.language,
        hash,
        secur: secure,
      },
      method: 'POST',
    })
  }

  fetchBanners = (
    page: number = 1,
    limit: number = 5,
  ): Promise<RestResponse<Banners>> => {
    try {
      return this.makeRequest.newRequest<Banners>({
        endpoint: `v2/banners`,
        bodyHeaders: {
          pagination: {page: page, limit: limit},
        },
      })
    } catch (e) {
      console.error('fetchBanners error => ', e)
      return Promise.resolve({error: 'internal_server_error', status: 500})
    }
  }

  fetchList = async (
    payload: FetchAdvertisesPayload,
  ): Promise<RestResponse<AdvertiseListResponse>> => {
    const filterQuery = this.filterMapper.prepareAdsFilterQuery(
      payload.filter,
      {
        advHash: payload.advHash,
        cacheId: payload.cacheId,
      },
    )
    filterQuery.onlyAggregation = payload.onlyAggregation
    return this.makeRequest.newRequest<AdvertiseListResponse>(
      {
        endpoint: 'v2/products/search',
        data: filterQuery,
        bodyHeaders: {
          pagination: {
            limit: payload.limit,
            page: payload.page ?? 1,
          },
        },
      },
      (rawData) => ({
        data: rawData.data,
        aggregatedFields: rawData.aggregatedFields,
        rootCategoryId: rawData.rootCategoryId,
      }),
    )
  }

  fetchDetail = async (
    id: string,
    includeTitleFields: boolean = false,
  ): Promise<RestResponse<AdvertiseDetail>> => {
    try {
      return this.makeRequest.newRequest<AdvertiseDetail>(
        {
          endpoint: `v2/advert/${id}`,
          data: {includeTitleFields},
        },
        dataResponseConverter,
      )
    } catch (e) {
      console.error('restFetchProduct error => ', e)
      return {error: 'internal_server_error', status: 500}
    }
  }

  fetchUserArchiveProducts = async (
    payload: RestFetchUserProductsPayload,
  ): Promise<RestResponse<Array<AdvertiseListItemModel>>> => {
    const params = {
      appId: this.storage.platform,
      cacheId: payload.cacheId,
    }

    return this.makeRequest.newRequest<Array<AdvertiseListItemModel>>(
      {
        endpoint: `v2/user/archived`,
        data: params,
        bodyHeaders: {
          pagination: {page: payload.page, limit: 20},
          user: {hash: payload.userId},
        },
      },
      dataResponseConverter,
    )
  }

  fetchUserSaleProducts = async (
    payload: RestFetchUserProductsPayload,
  ): Promise<RestResponse<Array<AdvertiseListItemModel>>> => {
    const params = {
      appId: this.storage.platform,
      cacheId: payload.cacheId,
    }

    return this.makeRequest.newRequest<Array<AdvertiseListItemModel>>(
      {
        endpoint: `v2/user/${payload.userId}/for-sale`,
        data: params,
        bodyHeaders: {
          pagination: {page: payload.page, limit: 20},
          user: {hash: payload.userId},
        },
      },
      dataResponseConverter,
    )
  }

  fetchUserSoldProducts = async (
    payload: RestFetchUserProductsPayload,
  ): Promise<RestResponse<Array<AdvertiseListItemModel>>> => {
    const params = {
      appId: this.storage.platform,
      cacheId: payload.cacheId,
    }

    return this.makeRequest.newRequest<Array<AdvertiseListItemModel>>(
      {
        endpoint: `v2/user/${payload.userId}/sold`,
        data: params,
        bodyHeaders: {
          pagination: {page: payload.page, limit: 20},
          user: {hash: payload.userId},
        },
      },
      dataResponseConverter,
    )
  }

  fetchUserOnModerationProducts = async (
    payload: RestFetchUserProductsPayload,
  ): Promise<RestResponse<Array<AdvertiseListItemModel>>> => {
    const params = {
      appId: this.storage.platform,
      cacheId: payload.cacheId,
    }

    return this.makeRequest.newRequest<Array<AdvertiseListItemModel>>(
      {
        endpoint: `v2/user/moderation`,
        data: params,
        bodyHeaders: {
          pagination: {page: payload.page, limit: 20},
          user: {hash: payload.userId},
        },
      },
      dataResponseConverter,
    )
  }

  subscriptionProducts = async (payload: RestFetchUserProductsPayload) => {
    return this.makeRequest.newRequest<Array<AdvertiseListItemModel>>(
      {
        endpoint: 'v2/products/search',
        data: {
          appId: this.storage.platform,
          onlyFromSubscribed: true,
          cacheId: payload.cacheId,
        },
        bodyHeaders: {
          pagination: {page: payload.page, limit: 20},
          user: {hash: payload.userId},
        },
      },
      dataResponseConverter,
    )
  }

  fetchUserFavorites = async (
    payload: RestFetchUserProductsPayload,
  ): Promise<RestResponse<Array<AdvertiseListItemModel>>> => {
    const params = {
      appId: this.storage.platform,
      cacheId: payload.cacheId,
    }

    return this.makeRequest.newRequest<Array<AdvertiseListItemModel>>(
      {
        endpoint: 'v2/user/favorites',
        data: params,
        bodyHeaders: {
          pagination: {page: payload.page, limit: 20},
          user: {hash: payload.userId},
        },
      },
      dataResponseConverter,
    )
  }

  toggleFavorite = async (
    id: AdvertiseHash,
    operation: 'add' | 'delete',
  ): Promise<RestResponse<any>> => {
    return this.makeRequest.newRequest(
      {
        endpoint: `v2/advert/${id}/favorite`,
        data: {operation},
      },
      dataResponseConverter,
    )
  }

  deactivate = async (
    hash: AdvertiseHash,
    soldMode: RemoveFromSaleType,
  ): Promise<RestResponse<any>> => {
    const response = await this.makeRequest.newRequest<any>(
      {
        endpoint: `v2/advert/${hash}/deactivate`,
        data: {soldMode},
      },
      dataResponseConverter,
    )
    if (response.error) throw new Error(response.error)
    return response
  }

  activate = async (hash: AdvertiseHash): Promise<RestResponse<any>> => {
    const response = await this.makeRequest.newRequest<any>(
      {
        endpoint: `v2/advert/${hash}/activate`,
      },
      dataResponseConverter,
    )
    if (response.error) throw new Error(response.error)
    return response
  }

  delete = async (hash: AdvertiseHash): Promise<RestResponse<any>> => {
    const response = await this.makeRequest.newRequest<any>(
      {
        endpoint: `v2/advert/${hash}/delete`,
      },
      dataResponseConverter,
    )
    if (response.error) throw new Error(response.error)
    return response
  }

  purchase = async (
    hash: AdvertiseHash,
  ): Promise<RestResponse<{transactionId: string} | {depositUrl: string}>> => {
    return this.makeRequest.newRequest(
      {
        endpoint: 'v2/payments/purchase',
        data: {advertHash: hash},
      },
      (r) => r,
    )
  }

  checkPhonePermissions = async (
    adHash: string,
    ip?: string,
  ): Promise<RestResponse<CheckPhonePermissions>> => {
    let headers
    if (ip) {
      headers = {
        'x-adverto-client-ip': ip,
      }
    }
    try {
      return this.makeRequest.newRequest<CheckPhonePermissions>(
        {
          method: 'POST',
          endpoint: 'v2/products/check-phone-permissions',
          data: {hash: adHash},
          headers,
        },
        (data) => data,
      )
    } catch (e) {
      console.error('check-phone-permissions error => ', e)
      return {error: 'internal_server_error', status: 500}
    }
  }

  refreshAdvert = async (adHash: string): Promise<RestResponse<any>> => {
    try {
      return this.makeRequest.newRequest<any>(
        {
          method: 'POST',
          endpoint: `v2/advert/${adHash}/refresh`,
          body: {},
        },
        (data) => data,
      )
    } catch (e) {
      console.error('refresh-advert error => ', e)
      return {error: 'internal_server_error', status: 500}
    }
  }
}
